// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';
import type { TourRoundSummary, SidebarSelection } from '../types';
import SidebarItem from './SidebarItem';

type Props = {
  round: TourRoundSummary;
  index: number;
  expanded: boolean;
  selection: SidebarSelection;
  onToggleExpand: () => void;
  onSelect: (selection: SidebarSelection) => void;
  onDelete: () => void;
};

export default function RoundSidebarSection({
  round,
  index,
  expanded,
  selection,
  onToggleExpand,
  onSelect,
  onDelete,
}: Props) {
  const roundLabel = round.name || `Round ${index + 1}`;
  const dateLabel = round.playDate || '';
  const canDelete = !round.status || round.status === 'draft';
  const deleteTooltip = canDelete ? `Delete ${roundLabel}` : 'This round has already started and cannot be removed';

  const deleteRef = useCallback((node: View | null) => {
    if (node) (node as unknown as HTMLElement).title = deleteTooltip;
  }, [deleteTooltip]);

  const isRoundItemSelected = (kind: SidebarSelection['kind']) => {
    if (!('roundId' in selection)) return false;
    return selection.kind === kind && selection.roundId === round.id;
  };

  return (
    <View style={styles.container}>
      <Pressable style={styles.header} onPress={() => { onToggleExpand(); onSelect({ kind: 'round-settings', roundId: round.id }); }}>
        <View style={styles.headerLeft}>
          <Text style={styles.expandIcon}>{expanded ? '▾' : '▸'}</Text>
          <View style={styles.headerText}>
            {dateLabel ? <Text style={styles.dateText}>{dateLabel}</Text> : null}
            <Text style={styles.roundName} numberOfLines={1}>{roundLabel}</Text>
          </View>
        </View>
        <Pressable
          ref={deleteRef}
          style={[styles.deleteButton, !canDelete && styles.deleteButtonDisabled]}
          onPress={(e) => {
            e.stopPropagation();
            if (canDelete) onDelete();
          }}
          accessibilityLabel={deleteTooltip}
        >
          <Text style={[styles.deleteIcon, !canDelete && styles.deleteIconDisabled]}>×</Text>
        </Pressable>
      </Pressable>
      {expanded && (
        <View style={styles.subItems}>
          <SidebarItem
            label="Settings"
            active={isRoundItemSelected('round-settings')}
            indented
            onPress={() => onSelect({ kind: 'round-settings', roundId: round.id })}
          />
          <SidebarItem
            label="Players"
            active={isRoundItemSelected('round-players')}
            indented
            onPress={() => onSelect({ kind: 'round-players', roundId: round.id })}
          />
          <SidebarItem
            label="Games"
            active={isRoundItemSelected('round-games')}
            indented
            onPress={() => onSelect({ kind: 'round-games', roundId: round.id })}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 2,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  expandIcon: {
    fontSize: 12,
    color: appColors.text.muted,
    width: 14,
  },
  headerText: {
    flex: 1,
  },
  dateText: {
    fontSize: 11,
    color: appColors.text.muted,
    fontWeight: '500',
  },
  roundName: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  deleteButton: {
    width: 26,
    height: 26,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(215,63,78,0.2)',
    borderWidth: 1,
    borderColor: 'rgba(215,63,78,0.4)',
  },
  deleteButtonDisabled: {
    backgroundColor: 'rgba(100,100,100,0.1)',
    borderColor: 'rgba(100,100,100,0.2)',
    cursor: 'default',
  } as any,
  deleteIcon: {
    fontSize: 15,
    fontWeight: '700',
    color: appColors.status.danger,
    lineHeight: 17,
  },
  deleteIconDisabled: {
    color: appColors.text.muted,
    opacity: 0.4,
  },
  subItems: {
    marginLeft: 4,
  },
});
